import { Button, Modal, Toast, type ToastType } from "@legal-500/legal500-design-system";
import { useEffect, useState } from "react";
import type { ImageHashEntry } from "../../../api/imageHashes.service.js";
import { PageHeader } from "../../../components/PageHeader/index.js";
import {
  useClearImageHashes,
  useImageHashes,
  useImageHashStats,
} from "../../../hooks/useImageHashes.js";
import styles from "./ImageCache.module.css";

const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 300;
const HASH_PREVIEW_LENGTH = 12;

function formatBytes(bytes: number | null): string {
  if (bytes == null) return "—";
  if (bytes === 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / 1024 ** exponent;
  const formatted = exponent === 0 ? String(bytes) : value.toFixed(value >= 100 ? 0 : 1);
  return `${formatted} ${units[exponent]}`;
}

function formatDateTime(value: string | null): string {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString();
}

function CopyHashButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);

  async function handleClick(event: React.MouseEvent<HTMLButtonElement>) {
    event.stopPropagation();
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
    }
  }

  return (
    <button
      type="button"
      className={styles.copyButton}
      onClick={handleClick}
      title={copied ? "Copied!" : `Copy ${value}`}
      aria-label={`Copy hash ${value} to clipboard`}
    >
      {copied ? "Copied" : "Copy"}
    </button>
  );
}

export default function ImageCachePage() {
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [offset, setOffset] = useState(0);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);

  // Debounce the search box into the query param used by the list hook.
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setSearch(searchInput.trim());
      setOffset(0);
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  const {
    data: listData,
    loading: listLoading,
    error: listError,
    refetch: refetchList,
    isRefetching,
  } = useImageHashes({ limit: PAGE_SIZE, offset, search: search || undefined });

  const { data: stats, error: statsError, refetch: refetchStats } = useImageHashStats();

  const { mutate: clearCache, loading: clearing } = useClearImageHashes();

  const items = listData?.items ?? [];
  const pagination = listData?.pagination;

  async function handleClearCache() {
    try {
      const response = await clearCache();
      setConfirmOpen(false);
      setOffset(0);
      await Promise.all([refetchList(), refetchStats()]);
      setToast({
        message: `Cleared ${response.deleted} cached image${response.deleted === 1 ? "" : "s"}.`,
        type: "success",
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setToast({ message, type: "error" });
    }
  }

  function handlePrev() {
    setOffset(prev => Math.max(0, prev - PAGE_SIZE));
  }

  function handleNext() {
    if (!pagination?.has_more) return;
    setOffset(prev => prev + PAGE_SIZE);
  }

  function renderTable() {
    if (listLoading && !isRefetching) {
      return <div className={styles.stateMessage}>Loading cached images…</div>;
    }

    if (listError) {
      return (
        <div className={styles.stateMessage}>
          <div className={styles.errorBanner}>{listError.message}</div>
          <Button color="default" variant="outlined" onClick={() => refetchList()}>
            Retry
          </Button>
        </div>
      );
    }

    if (items.length === 0) {
      return (
        <div className={styles.stateMessage}>
          {search
            ? "No cached images match your search."
            : "No images have been cached yet. They are populated the next time wp-syncer's profiles backfill runs."}
        </div>
      );
    }

    return (
      <table className={styles.table}>
        <thead>
          <tr>
            <th>URL</th>
            <th>Filename</th>
            <th>Content type</th>
            <th>Size</th>
            <th>Hash</th>
            <th>Created</th>
            <th>Last seen</th>
          </tr>
        </thead>
        <tbody>
          {items.map((entry: ImageHashEntry) => (
            <tr key={`${entry.hash}:${entry.url}`}>
              <td className={styles.urlCell} title={entry.url}>
                {entry.url}
              </td>
              <td className={styles.filenameCell} title={entry.filename}>
                {entry.filename}
              </td>
              <td>{entry.content_type ?? "—"}</td>
              <td>{formatBytes(entry.byte_size)}</td>
              <td className={styles.hashCell}>
                <span className={styles.mono} title={entry.hash}>
                  {entry.hash.slice(0, HASH_PREVIEW_LENGTH)}
                  {entry.hash.length > HASH_PREVIEW_LENGTH ? "…" : ""}
                </span>
                <CopyHashButton value={entry.hash} />
              </td>
              <td>{formatDateTime(entry.created_at)}</td>
              <td>{formatDateTime(entry.last_seen_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  return (
    <div className={styles.page}>
      <PageHeader
        title="WordPress Image Cache"
        subtitle="Cached WordPress source image URL → Azure blob hash. Used by wp-syncer to skip re-downloading images."
      />

      {statsError && <div className={styles.errorBanner}>{statsError.message}</div>}

      <div className={styles.cards}>
        <div className={styles.card}>
          <span className={styles.cardLabel}>Total cached images</span>
          <span className={styles.cardNum}>{(stats?.total ?? 0).toLocaleString()}</span>
        </div>
        <div className={styles.card}>
          <span className={styles.cardLabel}>Total size</span>
          <span className={styles.cardNum}>{formatBytes(stats?.total_bytes ?? 0)}</span>
        </div>
        <div className={styles.card}>
          <span className={styles.cardLabel}>By content type</span>
          <div className={styles.breakdown}>
            {stats && stats.by_content_type.length > 0 ? (
              stats.by_content_type.map(row => (
                <div key={row.content_type ?? "unknown"} className={styles.breakdownRow}>
                  <span className={styles.breakdownType}>{row.content_type ?? "unknown"}</span>
                  <span className={styles.breakdownCount}>{row.count.toLocaleString()}</span>
                </div>
              ))
            ) : (
              <span className={styles.breakdownEmpty}>—</span>
            )}
          </div>
        </div>
        <div className={styles.card}>
          <span className={styles.cardLabel}>Oldest / newest</span>
          <div className={styles.cardMeta}>
            <div>Oldest: {formatDateTime(stats?.oldest_created_at ?? null)}</div>
            <div>Newest: {formatDateTime(stats?.newest_last_seen_at ?? null)}</div>
          </div>
        </div>
      </div>

      <div className={styles.toolbar}>
        <input
          type="search"
          className={styles.searchInput}
          placeholder="Search by URL, filename or hash…"
          value={searchInput}
          onChange={e => setSearchInput(e.target.value)}
          aria-label="Search cached images"
        />
        {pagination && <span className={styles.rowCount}>{pagination.total} entries</span>}
        <Button
          color="error"
          variant="contained"
          onClick={() => setConfirmOpen(true)}
          disabled={clearing}
        >
          {clearing ? "Clearing…" : "Clear cache"}
        </Button>
      </div>

      <div className={styles.mainSection}>
        <div className={styles.tableContainer}>{renderTable()}</div>

        {pagination && (pagination.has_more || offset > 0) && (
          <div className={styles.pagination}>
            <button
              type="button"
              className={styles.pageBtn}
              disabled={offset === 0}
              onClick={handlePrev}
            >
              ← Prev
            </button>
            <span className={styles.pageInfo}>
              {pagination.total === 0
                ? "0 of 0"
                : `${offset + 1}–${Math.min(offset + PAGE_SIZE, pagination.total)} of ${pagination.total}`}
            </span>
            <button
              type="button"
              className={styles.pageBtn}
              disabled={!pagination.has_more}
              onClick={handleNext}
            >
              Next →
            </button>
          </div>
        )}
      </div>

      <Modal
        isOpen={confirmOpen}
        onClose={() => (clearing ? undefined : setConfirmOpen(false))}
        title="Clear WordPress image cache?"
        size="small"
        blurBackground
      >
        <div className={styles.modalBody}>
          <p>
            This deletes every cached URL → blob-hash mapping. The next wp-syncer profiles backfill
            will re-download and re-upload every image, which can be slow and increases Azure blob
            churn. Use only when you need to force a full re-upload.
          </p>
          <div className={styles.modalActions}>
            <Button
              color="default"
              variant="outlined"
              onClick={() => setConfirmOpen(false)}
              disabled={clearing}
            >
              Cancel
            </Button>
            <Button
              color="error"
              variant="contained"
              onClick={handleClearCache}
              disabled={clearing}
            >
              {clearing ? "Clearing…" : "Clear cache"}
            </Button>
          </div>
        </div>
      </Modal>

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}
